// Copyright (c) Docugami, Inc. All rights reserved.

import { ProviderId, StorageProviderAuthenticationResponse } from "networking/StorageProviders";
import { IStorageManager } from "./StorageManager";
import { Event, IEvent } from "utils/Event"
import { OperationCancelledError, RetryConfiguration, RetryPromise } from "utils/RetryPromise";
import { OAuthRequestStatus, UserAuthRequest, Token } from "networking/Authentication";

export class TokenAccessor {
    private readonly _storageManager: IStorageManager;
    private readonly _providerId: ProviderId;

    private _status: OAuthRequestStatus = OAuthRequestStatus.None;
    private _abortController: AbortController | null = null;

    private _onUserAuthRequested: Event<UserAuthRequest> = new Event();
    private _onStatusChanged: Event<OAuthRequestStatus> = new Event();

    public get onUserAuthRequested(): IEvent<UserAuthRequest> { return this._onUserAuthRequested; }
    public get onStatusChanged(): IEvent<OAuthRequestStatus> { return this._onStatusChanged; }

    public get status(): OAuthRequestStatus { return this._status; }
    public get isRunning(): boolean { return !!this._abortController; }

    constructor(id: ProviderId, storageManager: IStorageManager) {
        this._providerId = id;
        this._storageManager = storageManager;
    }

    async getToken(): Promise<Token | null> {
        try {
            this._abortController = new AbortController();
            const tokenRequest = await this._storageManager.requestToken(this._providerId);

            if (!!tokenRequest.accessToken) {
                this.handleResponse();
                return tokenRequest.accessToken;
            }

            if (!tokenRequest.request) {
                throw new Error("Neither accessToken nor requestId received for token request.");
            }

            // User authentication is required to continue
            this.setStatus(OAuthRequestStatus.Initiated);
            this._onUserAuthRequested.raise(tokenRequest.request);

            // Kick off our status retry requests
            const config: Partial<RetryConfiguration<StorageProviderAuthenticationResponse>> = {
                resultHandler: retryResult => retryResult.status === OAuthRequestStatus.Initiated || retryResult.status === OAuthRequestStatus.Authorizing,
                intervalMs: 1000,
                maxRetryCount: 300
            };

            const authStatusRequest = new RetryPromise<StorageProviderAuthenticationResponse>(
                () => this._storageManager.authenticate(this._providerId, tokenRequest.request!.id),
                config
            );

            const unregister = authStatusRequest.onExecuted.addListener(currentResult => {
                if (!!currentResult.result) {
                    this.handleResponse(currentResult.result.status);
                }
            });

            try {
                const result = await authStatusRequest.execute(this._abortController.signal);
                this.handleResponse(result.status);
                return result.accessToken ?? null;
            }
            catch (error) {
                if (error instanceof OperationCancelledError) {
                    return null;
                }

                throw error;
            }
            finally {
                unregister();
            }
        }
        finally {
            this.reset();
        }
    }

    cancel() {
        this.reset();
    }

    private setStatus(status: OAuthRequestStatus) {
        if (status !== this._status) {
            this._status = status;
            this._onStatusChanged.raise(this._status);
        }
    }

    private handleResponse(status?: OAuthRequestStatus) {
        if (!!status) {
            this.setStatus(status);
        }
    }

    private reset() {
        this._abortController?.abort();
        this._abortController = null;
        this.setStatus(OAuthRequestStatus.None);
    }
}
